import { buildApiUrl, Type, sendPutRequest } from '../api'
import { type UpdateAssetSettings } from './schemas'
import { objectWithoutKeys, roundToFractionalCurrencyPrecision } from '@/helpers'
import { AssetType } from '@/features/assets/constants'
import type { DistributiveOmit } from '@/shared'

export async function saveAssetSettings(assetCode: string, settings: DistributiveOmit<UpdateAssetSettings, 'contacts'|'tariffProfile'>) {
  if (settings.assetType !== AssetType.Wheeling && settings.assetType !== AssetType.Solar) {
    throw new Error('Unexpected asset type when saving settings.')
  }

  const url = buildApiUrl(Type.AssetManagement, `asset/${assetCode}/settings`)
  const data = {
    ...settings,
    contractTerms: {
      ...objectWithoutKeys(settings.contractTerms, [
        'firstEscalationMonth',
        'firstEscalationYear',
        ...(
          settings.assetType === AssetType.Wheeling
            ? [
              'renewableEnergyCertificatesIssuanceFeeInDollars',
              'renewableEnergyCertificatesPlatformFeeInRands',
              'renewableEnergyCertificatesTransferFeeInRands'
            ] satisfies (keyof typeof settings.contractTerms)[]
            : []
        )
      ]),
      firstEscalationMonth: settings.assetType != AssetType.Wheeling ?
        `${settings.contractTerms.firstEscalationYear}-${(settings.contractTerms.firstEscalationMonth ?? 0).toString().padStart(2, '0')}-01`
        : undefined,
      // Intentionally using `as ...` to ensure TS is aware of the object properties.
      renewableEnergyCertificatesIssuanceFeeInCents: undefined as number|undefined,
      renewableEnergyCertificatesTransferFeeInCents: undefined as number|undefined,
      renewableEnergyCertificatesPlatformFeeInCents: undefined as number|undefined,
      renewableEnergyCertificatesEffectiveDate: undefined as string|undefined
    },
    utilityProvider: {
      ...objectWithoutKeys(settings.utilityProvider, ['blendedTariff']),
      blendedTariffInCents: roundToFractionalCurrencyPrecision(settings.utilityProvider.blendedTariff * 100)
    }
  }

  if (settings.assetType === AssetType.Wheeling) {
    data.contractTerms.renewableEnergyCertificatesEffectiveDate = settings.contractTerms.renewableEnergyCertificatesEffectiveDate != null
      ? `${settings.contractTerms.renewableEnergyCertificatesEffectiveDate.toISOString().split('T')[0]}`
      : undefined;

    data.contractTerms.renewableEnergyCertificatesIssuanceFeeInCents = settings.contractTerms.renewableEnergyCertificatesIssuanceFeeInDollars != null
      ? settings.contractTerms.renewableEnergyCertificatesIssuanceFeeInDollars * 100
      : undefined;

    data.contractTerms.renewableEnergyCertificatesTransferFeeInCents = settings.contractTerms.renewableEnergyCertificatesTransferFeeInRands != null
      ? settings.contractTerms.renewableEnergyCertificatesTransferFeeInRands * 100
      : undefined;

    data.contractTerms.renewableEnergyCertificatesPlatformFeeInCents = settings.contractTerms.renewableEnergyCertificatesPlatformFeeInRands != null
      ? settings.contractTerms.renewableEnergyCertificatesPlatformFeeInRands * 100
      : undefined;
  }

  await sendPutRequest(url, { data })
}
